import React, { useState, useEffect } from "react";
import "./Products.css";
import { getAuth, signInAnonymously } from "firebase/auth";
import config from "../../config";
import UpdateProduct from "./ManageProduct";
import CreateProductForm from "./CreateProductForm";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [inactiveProducts, setInactiveProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJsonForm, setShowJsonForm] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState("");
  const [showManageProduct, setShowManageProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState("active"); // 'active' | 'review'

  useEffect(() => {
    fetchProducts();
    fetchInactiveProducts();
    // Keep anonymous sign-in for Firebase image usage, etc.
    signInAnonymously(getAuth()).catch(console.error);
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/products`);
      if (!res.ok) throw new Error("Failed to fetch products");
      const data = await res.json();
      setProducts(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshProducts = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      console.error("Failed to refresh products:", err);
    }
  };

  const fetchInactiveProducts = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/products/admin/inactive`);
      if (res.ok) {
        const data = await res.json();
        setInactiveProducts(data);
      }
    } catch (err) {
      console.error("Failed to fetch inactive products:", err);
    }
  };

  // 🔹 Approve (activate) with optional comment
  const approveProduct = async (productId, comment) => {
    try {
      const safeComment =
        comment && comment.trim().length > 0 ? comment.trim() : "Approved";
      const url = `${config.baseUrl}/api/products/${productId}/active?active=true&adminComment=${encodeURIComponent(
        safeComment
      )}`;
      console.log("Approving product with URL:", url);

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
        },
      });

      console.log("Response status (approve):", res.status);

      if (res.ok) {
        setSuccessMessage("Product approved and activated!");
        setTimeout(() => setSuccessMessage(""), 4000);
        await fetchInactiveProducts();
        await fetchProducts();
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error("Error response (approve):", errorData);
        throw new Error(
          errorData.message ||
            `Failed to approve product (Status: ${res.status})`
        );
      }
    } catch (err) {
      console.error("Approve product error:", err);
      alert("Error approving product: " + err.message);
    }
  };

  // 🔹 Reject (stay inactive) with MANDATORY comment
  const rejectProduct = async (productId, comment) => {
    try {
      if (!comment || !comment.trim()) {
        alert("Please add a comment when rejecting a product.");
        return;
      }

      const url = `${config.baseUrl}/api/products/${productId}/active?active=false&adminComment=${encodeURIComponent(
        comment.trim()
      )}`;
      console.log("Rejecting product with URL:", url);

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
        },
      });

      console.log("Response status (reject):", res.status);

      if (res.ok) {
        setSuccessMessage("Product rejected.");
        setTimeout(() => setSuccessMessage(""), 4000);
        await fetchInactiveProducts();
        await fetchProducts();
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error("Error response (reject):", errorData);
        throw new Error(
          errorData.message ||
            `Failed to reject product (Status: ${res.status})`
        );
      }
    } catch (err) {
      console.error("Reject product error:", err);
      alert("Error rejecting product: " + err.message);
    }
  };

  const deleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const res = await fetch(
          `${config.baseUrl}/api/products/${productId}`,
          {
            method: "DELETE",
          }
        );
        if (!res.ok) throw new Error("Failed to delete product");
        const message = await res.text();
        setProducts((prev) =>
          prev.filter((prod) => prod.productId !== productId)
        );
        setSuccessMessage(message || "Product deleted successfully.");
        setTimeout(() => setSuccessMessage(""), 4000);
      } catch (err) {
        alert("Error deleting product: " + err.message);
      }
    }
  };

  const toggleExpanded = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  /* ---------------- ACTIVE PRODUCTS ---------------- */

  // Only active products should appear in Active tab
  const activeProducts = products.filter((p) => p.active);

  const filteredActiveProducts = activeProducts.filter((product) => {
    const productName = product.productName || "";
    const productDescription = product.productDescription || "";
    const productId = product.productId ? String(product.productId) : "";

    const search = searchTerm.trim().toLowerCase();

    const matchesSearch =
      !search ||
      productName.toLowerCase().includes(search) ||
      productDescription.toLowerCase().includes(search) ||
      productId.includes(search);

    const matchesCategory =
      !categoryFilter ||
      product.category?.name === categoryFilter ||
      product.categoryName === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  /* ---------------- INACTIVE = PENDING + REJECTED ---------------- */

  const pendingReviewProducts = inactiveProducts.filter(
    (p) => !p.adminComment || p.adminComment.trim() === ""
  );

  const rejectedProducts = inactiveProducts.filter(
    (p) => p.adminComment && p.adminComment.trim() !== ""
  );

  if (loading) return <div className="loading">Loading products...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="products-section">
      {/* HEADER */}
      <div className="section-header">
        <div className="header-content">
          <h2>Product Management</h2>
          <p>Manage your complete product catalog with variants and images</p>
        </div>
        <div className="header-actions">
          <button className="btn-json" onClick={() => setShowJsonForm(true)}>
            <span className="btn-icon">📋</span>
            Add JSON Product
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            <span className="btn-icon">➕</span>
            Add Product
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="tabs-bar">
        <button
          className={`tab-button ${activeTab === "active" ? "active" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          <span className="tab-icon">📦</span>
          Active Products ({activeProducts.length})
        </button>
        <button
          className={`tab-button ${activeTab === "review" ? "active" : ""}`}
          onClick={() => setActiveTab("review")}
        >
          <span className="tab-icon">📋</span>
          Review &amp; Approve ({inactiveProducts.length})
        </button>
      </div>

      {/* -------- ACTIVE TAB -------- */}
      {activeTab === "active" && (
        <>
          <div className="filters-bar">
            <input
              type="text"
              placeholder="Search products by name, description, or product ID..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="Boys">Boys</option>
              <option value="Girls">Girls</option>
              <option value="Ladies">Ladies</option>
              <option value="Men">Men</option>
              <option value="Women">Women</option>
            </select>
          </div>

          <div className="products-grid">
            {filteredActiveProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">📦</div>
                <h3>No active products found</h3>
                <p>Start by adding your first product</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  Add Your First Product
                </button>
              </div>
            ) : (
              filteredActiveProducts.map((product) => (
                <ProductCard
                  key={product.productId}
                  product={product}
                  isExpanded={expandedProducts.has(product.productId)}
                  onToggleExpanded={() => toggleExpanded(product.productId)}
                  onDelete={() => deleteProduct(product.productId)}
                  onManage={() => {
                    setSelectedProduct(product);
                    setShowManageProduct(true);
                  }}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* -------- REVIEW / REJECT TAB -------- */}
      {activeTab === "review" && (
        <>
          {/* Pending Review */}
          <div className="review-section">
            <h3>Pending Review &amp; Approval</h3>
            {pendingReviewProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">✓</div>
                <h3>No pending products</h3>
                <p>All submitted products are either approved or rejected.</p>
              </div>
            ) : (
              <div className="products-grid">
                {pendingReviewProducts.map((product) => (
                  <InactiveProductCard
                    key={product.productId}
                    product={product}
                    onApprove={(comment) =>
                      approveProduct(product.productId, comment)
                    }
                    onReject={(comment) =>
                      rejectProduct(product.productId, comment)
                    }
                    isExpanded={expandedProducts.has(product.productId)}
                    onToggleExpanded={() =>
                      toggleExpanded(product.productId)
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Rejected */}
          <div className="review-section">
            <h3>Rejected Products</h3>
            {rejectedProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">🧺</div>
                <h3>No rejected products</h3>
                <p>Rejections will appear here with admin comments.</p>
              </div>
            ) : (
              <div className="products-grid">
                {rejectedProducts.map((product) => (
                  <RejectedProductCard
                    key={product.productId}
                    product={product}
                    isExpanded={expandedProducts.has(product.productId)}
                    onToggleExpanded={() =>
                      toggleExpanded(product.productId)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* MODALS */}
      {showCreateForm && (
        <CreateProductForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            refreshProducts();
            fetchInactiveProducts();
          }}
        />
      )}

      {showJsonForm && (
        <JsonBulkUploadForm
          onClose={() => setShowJsonForm(false)}
          onSuccess={refreshProducts}
        />
      )}

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {showManageProduct && selectedProduct && (
        <UpdateProduct
          product={selectedProduct}
          onClose={() => {
            setShowManageProduct(false);
            setSelectedProduct(null);
          }}
          onProductUpdated={() => {
            refreshProducts();
            fetchInactiveProducts();
          }}
        />
      )}
    </div>
  );
}

/* ---------- Product Card (ACTIVE) ---------- */
function ProductCard({
  product,
  isExpanded,
  onToggleExpanded,
  onDelete,
  onManage,
}) {
  const mainImage =
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.image_url ||
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.imageUrl ||
    product.images?.[0]?.image_url ||
    product.images?.[0]?.imageUrl ||
    "https://via.placeholder.com/200x200?text=No+Image";

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge active">Active</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Color:</span>
            <span className="meta-value">{product.color || "N/A"}</span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{product.variants?.length || 0}</span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.variants?.length > 0 && (
          <div className="variants-section">
            <h4>Variants &amp; Pricing</h4>
            <div className="variants-grid">
              {product.variants.map((variant) => (
                <div key={variant.variantId || variant.id} className="variant-card">
                  <div className="variant-size">
                    {variant.size?.code ||
                      variant.sizeName ||
                      variant.size?.name ||
                      "N/A"}
                  </div>
                  <div className="variant-sku">{variant.sku}</div>
                  <div className="variant-prices">
                    <span className="original-price">
                      ₹{Number(variant.originalPrice || 0).toFixed(2)}
                    </span>
                    <span className="offer-price">
                      ₹{Number(variant.offerPrice || variant.originalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`variant-stock ${
                      variant.stockQuantity < 10 ? "low-stock" : ""
                    }`}
                  >
                    Stock: {variant.stockQuantity}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && product.images?.length > 0 && (
          <div className="images-section">
            <h4>Product Images</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ Expand Details"}
          </button>
          <button className="btn-delete" onClick={onDelete}>
            🗑️ Delete
          </button>
          <button className="btn-manage" onClick={onManage}>
            ⚙️ Update Product
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- JSON Bulk Upload Form ---------- */
function JsonBulkUploadForm({ onClose, onSuccess }) {
  const [jsonInput, setJsonInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sampleJson = {
    productName: "Boys Classic Polo Shirt",
    color: "Navy Blue",
    productDescription:
      "Soft cotton polo with ribbed collar — everyday comfort.",
    categoryName: "Boys",
    subCategoryName: "Shirts",
    variants: [
      {
        sku: "KW-POLO-NV-S",
        sizeName: "S",
        originalPrice: 599.0,
        discount: 10,
        offerPrice: 539.1,
        stockQuantity: 50,
        vendorId: 101,
      },
    ],
  };

  const handleSubmit = async () => {
    if (!jsonInput.trim()) {
      setError("Please enter JSON data");
      return;
    }

    try {
      const productData = JSON.parse(jsonInput);
      setLoading(true);
      setError("");

      const productResponse = await fetch(`${config.baseUrl}/api/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productName: productData.productName,
          color: productData.color,
          productDescription: productData.productDescription,
          categoryName: productData.categoryName,
          subCategoryName: productData.subCategoryName,
        }),
      });

      if (!productResponse.ok) throw new Error("Failed to create product");
      const product = await productResponse.json();
      const productId = product.productId || product.id;

      if (productData.variants?.length) {
        const variantResponse = await fetch(
          `${config.baseUrl}/api/products/${productId}/variants`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(productData.variants),
          }
        );

        if (!variantResponse.ok)
          throw new Error("Failed to create variants");
      }

      alert("Product created successfully from JSON!");
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create Product from JSON</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Product JSON Data</label>
            <textarea
              rows="20"
              className="json-textarea"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder={JSON.stringify(sampleJson, null, 2)}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Product"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Inactive Product Card (PENDING REVIEW) ---------- */
function InactiveProductCard({
  product,
  onApprove,
  onReject,
  isExpanded,
  onToggleExpanded,
}) {
  const mainImage =
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.image_url ||
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.imageUrl ||
    product.images?.[0]?.image_url ||
    product.images?.[0]?.imageUrl ||
    "https://via.placeholder.com/200x200?text=No+Image";

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  const [comment, setComment] = useState("Approved");

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge pending">Pending Review</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Color:</span>
            <span className="meta-value">{product.color || "N/A"}</span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{product.variants?.length || 0}</span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.variants?.length > 0 && (
          <div className="variants-section">
            <h4>Variants &amp; Pricing</h4>
            <div className="variants-grid">
              {product.variants.map((variant) => (
                <div key={variant.variantId || variant.id} className="variant-card">
                  <div className="variant-size">
                    {variant.size?.code ||
                      variant.sizeName ||
                      variant.size?.name ||
                      "N/A"}
                  </div>
                  <div className="variant-sku">{variant.sku}</div>
                  <div className="variant-prices">
                    <span className="original-price">
                      ₹{Number(variant.originalPrice || 0).toFixed(2)}
                    </span>
                    <span className="offer-price">
                      ₹{Number(variant.offerPrice || variant.originalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`variant-stock ${
                      variant.stockQuantity < 10 ? "low-stock" : ""
                    }`}
                  >
                    Stock: {variant.stockQuantity}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && product.images && product.images.length > 0 && (
          <div className="images-section">
            <h4>Product Images ({product.images.length})</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comment box */}
        <div className="review-comment">
          <label className="review-comment-label">Admin Comment</label>
          <textarea
            className="review-comment-textarea"
            placeholder="Eg. Images not clear, please re-upload high-resolution photos."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ Review Details"}
          </button>
          <button className="btn-approve" onClick={() => onApprove(comment)}>
            ✓ Approve &amp; Activate
          </button>
          <button className="btn-reject" onClick={() => onReject(comment)}>
            ✕ Reject
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Rejected Product Card (READ-ONLY) ---------- */
function RejectedProductCard({
  product,
  isExpanded,
  onToggleExpanded,
}) {
  const mainImage =
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.image_url ||
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.imageUrl ||
    product.images?.[0]?.image_url ||
    product.images?.[0]?.imageUrl ||
    "https://via.placeholder.com/200x200?text=No+Image";

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge inactive">Rejected</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Color:</span>
            <span className="meta-value">{product.color || "N/A"}</span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {product.variants?.length || 0}
            </span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.images && product.images.length > 0 && (
          <div className="images-section">
            <h4>Product Images ({product.images.length})</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="review-comment">
          <label className="review-comment-label">Admin Comment</label>
          <div className="review-comment-readonly">
            {product.adminComment || "-"}
          </div>
        </div>

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ View Details"}
          </button>
        </div>
      </div>
    </div>
  );
}
